import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  Button,
  Loading,
  Alert,
  SearchField,
  IconButton,
  Checkbox,
  Dialog,
  Menu,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { ACCOUNT_USERS } from '@elementary/common-ui/constants/queryKeys';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus.svg';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/20x20/trash.svg';
import * as api from 'shared/services/settingsApi';
import UserCard from 'settings/components/shared/UserCard';
import { GroupFormData } from '../types';

const Container = styled(FlexColumn)`
  padding: 24px 0;
  gap: 24px;
  height: 100%;
  overflow-y: auto;
`;

const SearchContainer = styled(Flex)`
  gap: 8px;
  align-items: center;
`;

const UserRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
  min-height: 54px;
`;

const AddUserButton = styled(Button)`
  min-width: 142px;
`;

const NoUsersMessage = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray2};
  padding: 24px 0;
`;

type Props = {
  hasExternalManager: boolean;
};

const UsersTab = ({ hasExternalManager }: Props) => {
  const methods = useFormContext<GroupFormData>();
  const { watch, setValue } = methods;
  const userIds = watch('userIds');
  const { data: allUsers, isLoading: isLoadingUsers } = useQuery(
    [ACCOUNT_USERS],
    () => api.fetchUsers(),
  );

  const [search, setSearch] = useState('');
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<string[]>([]);
  const [addUserDialogSearch, setAddUserDialogSearch] = useState('');

  const addUserToAdd = useCallback(
    (userId: string) => {
      setSelectedUsersToAdd([...selectedUsersToAdd, userId]);
    },
    [selectedUsersToAdd],
  );

  const removeUserFromAdd = useCallback(
    (userId: string) => {
      setSelectedUsersToAdd(selectedUsersToAdd.filter((id) => id !== userId));
    },
    [selectedUsersToAdd],
  );

  const currentUsers = useMemo(() => {
    if (!allUsers) return [];
    return allUsers.filter((user) => userIds.includes(user.id));
  }, [allUsers, userIds]);

  const availableUsers = useMemo(() => {
    if (!allUsers) return [];
    return allUsers.filter((user) => !userIds.includes(user.id));
  }, [allUsers, userIds]);

  const filteredCurrentUsers = useMemo(() => {
    if (!search.trim()) return currentUsers;
    const searchLower = search.toLowerCase();
    return currentUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower),
    );
  }, [currentUsers, search]);

  const filteredAvailableUsersForDialog = useMemo(() => {
    if (!addUserDialogSearch.trim()) return availableUsers;
    const searchLower = addUserDialogSearch.toLowerCase();
    return availableUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower),
    );
  }, [availableUsers, addUserDialogSearch]);

  const handleAddUsers = () => {
    setValue('userIds', [...userIds, ...selectedUsersToAdd]);
    setSelectedUsersToAdd([]);
    setShowAddUserDialog(false);
    setAddUserDialogSearch('');
    setSearch('');
  };

  const handleUserRemove = (userId: string) => {
    setValue(
      'userIds',
      userIds.filter((id) => id !== userId),
    );
  };

  if (isLoadingUsers) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  return (
    <Container>
      {hasExternalManager && (
        <Alert severity="info">
          This group is managed externally. Users cannot be added or removed
          from this interface.
        </Alert>
      )}

      <SearchContainer>
        <SearchField
          sx={{ flex: 1 }}
          onChange={(e) => setSearch(e.target.value)}
          value={search}
          placeholder="Search"
        />
        {!hasExternalManager && (
          <AddUserButton
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => {
              setShowAddUserDialog(true);
            }}
          >
            Add user
          </AddUserButton>
        )}
      </SearchContainer>

      <FlexColumn sx={{ gap: '4px' }}>
        {filteredCurrentUsers.length === 0 && !search ? (
          <NoUsersMessage>No users in this group</NoUsersMessage>
        ) : filteredCurrentUsers.length === 0 && search ? (
          <NoUsersMessage>
            No users found matching &quot;{search}&quot;
          </NoUsersMessage>
        ) : (
          filteredCurrentUsers.map((user) => (
            <UserRow key={user.id}>
              <UserCard id={user.id} name={user.name} email={user.email} />
              {!hasExternalManager && (
                <Menu
                  closeOnChange
                  menuButton={
                    <IconButton height="20px">
                      <MoreHorizIcon />
                    </IconButton>
                  }
                  options={[
                    {
                      label: 'Remove',
                      value: 'remove',
                      icon: <DeleteIcon />,
                    },
                  ]}
                  onChange={(value: string) => {
                    if (value === 'remove') {
                      handleUserRemove(user.id);
                    }
                  }}
                />
              )}
            </UserRow>
          ))
        )}
      </FlexColumn>

      <Dialog
        open={showAddUserDialog}
        onClose={() => {
          setShowAddUserDialog(false);
          setSelectedUsersToAdd([]);
          setAddUserDialogSearch('');
        }}
        onConfirmButtonClick={handleAddUsers}
        title="Add users"
        confirmText="Add"
        confirmDisabled={selectedUsersToAdd.length === 0}
        message={
          <FlexColumn sx={{ gap: 2, maxHeight: '400px' }}>
            <SearchField
              sx={{ width: '100%' }}
              onChange={(e) => setAddUserDialogSearch(e.target.value)}
              value={addUserDialogSearch}
              placeholder="Search users"
            />
            <FlexColumn sx={{ gap: 0, maxHeight: '350px', overflowY: 'auto' }}>
              {filteredAvailableUsersForDialog.length === 0 ? (
                <Typography sx={{ color: 'common.gray2', py: 2 }}>
                  {addUserDialogSearch
                    ? `No users found matching &quot;${addUserDialogSearch}&quot;`
                    : 'No available users to add'}
                </Typography>
              ) : (
                filteredAvailableUsersForDialog.map((user) => (
                  <UserRow key={user.id}>
                    <UserCard
                      id={user.id}
                      name={user.name}
                      email={user.email}
                    />
                    <Checkbox
                      checked={selectedUsersToAdd.includes(user.id)}
                      onChange={(_, checked) => {
                        if (checked) {
                          addUserToAdd(user.id);
                        } else {
                          removeUserFromAdd(user.id);
                        }
                      }}
                    />
                  </UserRow>
                ))
              )}
            </FlexColumn>
          </FlexColumn>
        }
      />
    </Container>
  );
};

UsersTab.displayName = 'UsersTab';

export default UsersTab;
